import { ManagedColumn, ManagedTableSorting, MultipleSelection, SingleSelection } from 'app/common/table/ManagedTable';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import Utils from 'app/common/util/Utils';
import { ProvidesKey } from 'app/model/client/CORE';
import { Flex } from 'app/components/common/Flex';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { SortingDirection } from '@devexpress/dx-react-grid';
import { Spin } from 'antd';

import './DataTable.less';

// NOTE: Modelled after ManagedTableProps to maintain backwards compatibility whilst existing ManagedTables are ported over.
export interface DataTableProps<T> {
    /** The data or records to show. */
    data: Array<T>;

    /** Columns to use as headers. */
    columns: Array<ManagedColumn<T>>;

    /** NOTE: Not currently used. Kept for backwards compatibility with ManagedTableProps. */
    keyProvider: ProvidesKey<T>;

    /** Current selection of items. 'SingleSelection' by default. */
    selection?: SingleSelection<T> | MultipleSelection<T>;

    /** Whether table data is still loading. */
    loading?: boolean;

    /** Specifies initial sorting in the uncontrolled mode. */
    defaultSorting?: Array<ManagedTableSorting>;

    /** Message to show when no data is present. */
    noDataMessage?: string;
}

export function DataTable<T>(props: DataTableProps<T>) {
    if (props.loading) {
        return (
            <Flex direction="vertical">
                <Spin spinning={true} tip="Loading data" style={{ flex: '1 1 auto', alignContent: 'center' }}></Spin>
            </Flex>
        );
    }

    /*
        NOTE: We don't have pagination yet, so showing e.g. 13.000 rows in a table is too heavy-weight performance-wise.
        The original table used virtual data for this and, whilst it could handle this amount of rows better, it
        performed poorly during re-renders instead.

        Working with anything above a certain amount of results is unpractical UX-wise in any case, with or without
        pagination. No one realistically browses more than a few pages of results without using filters or sorting to
        find what they are looking for, so until we have proper pagination, cap the displayed results visually.
    */
    const MAX_DISPLAYED_RESULTS = 100;

    const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
    const [sortDirection, setSortDirection] = useState<SortingDirection>('asc');

    const [sortColumn, setSortColumn] = useState<ManagedColumn<T> | null>(() => {
        if (!props.defaultSorting) {
            return null;
        }

        if (props.defaultSorting.length > 1) {
            throw new Error('Multi-sorting is currently not supported.');
        }

        return props.columns.find(x => x.header === props.defaultSorting[0].columnName);
    });

    const dataDependency = JSON.stringify(props.data);

    const sortedData = useMemo(() => {
        return sortColumn === null
            ? props.data
            : [...props.data].sort(
                  (a, b) =>
                      (JSON.stringify(sortColumn.getCellValue(a)) || '').localeCompare(
                          JSON.stringify(sortColumn.getCellValue(b) || ''),
                      ) * (sortDirection === 'asc' ? 1 : -1),
              );
    }, [dataDependency, sortColumn, sortDirection]);

    useEffect(() => {
        setLastSelectedIndex(null);
    }, [dataDependency]);

    return (
        <Flex direction="vertical">
            <div
                className="data-table"
                style={{
                    gridTemplateColumns: props.columns.map(column => `minmax(${column.width}, 100%)`).join(' '),
                }}>
                {props.columns.map(column => {
                    return (
                        <div
                            key={`HEADER_${column.extra?.name || column.header}`}
                            className="data-table__header-cell"
                            title={column.header}
                            onClick={() => {
                                if (column === sortColumn) {
                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                } else {
                                    setSortColumn(column);
                                    setSortDirection('asc');
                                }
                            }}>
                            <span className="data-table__header-cell__title">{column.header}</span>

                            {column === sortColumn &&
                                (sortDirection === 'asc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />)}
                        </div>
                    );
                })}
                {sortedData.slice(0, MAX_DISPLAYED_RESULTS).map((record, recordIndex) => {
                    const recordKey = props.keyProvider.getKey(record) as string | object;
                    const rowComponentKey = JSON.stringify(recordKey);

                    const isSelected =
                        props.selection &&
                        (props.selection?.selectionMode === 'single'
                            ? props.keyProvider.getKey(props.selection.selectedItem) === recordKey
                            : props.selection.selectedItems.find(x => props.keyProvider.getKey(x) === recordKey));

                    return (
                        <div className={`data-table__row ${isSelected ? '--active' : ''}`} key={rowComponentKey}>
                            {props.columns.map((column, columnIndex) => {
                                const cellValue = column.getCellValue(record);
                                const displayValue = column.extra?.formatter
                                    ? column.extra?.formatter(cellValue)
                                    : cellValue;

                                if (typeof displayValue === 'object' && !React.isValidElement(displayValue)) {
                                    throw new Error(
                                        `Cell values for column '${column.header}' should be transformed by a ` +
                                            'formatter in order to be displayed as objects cannot be displayed ' +
                                            'directly by React',
                                    );
                                }

                                return (
                                    <div
                                        key={`${rowComponentKey}_${column.extra?.name || column.header}`}
                                        className="data-table__cell"
                                        title={typeof displayValue === 'string' ? displayValue : ''}
                                        style={{ gridRow: 2 + recordIndex * 1, gridColumn: columnIndex + 1 }}
                                        onClick={event => {
                                            event.preventDefault();
                                            event.stopPropagation();

                                            if (props.selection?.selectionMode !== 'multiple') {
                                                props.selection?.onSelectionChange?.(record);
                                            } else if (event.shiftKey) {
                                                document.getSelection().removeAllRanges(); // Don't select text.

                                                const lastIndex =
                                                    lastSelectedIndex !== null ? lastSelectedIndex : recordIndex;

                                                const selectedIndex = recordIndex;

                                                const startIndex =
                                                    lastIndex < selectedIndex ? lastIndex : selectedIndex;

                                                const endIndex =
                                                    (lastIndex < selectedIndex ? selectedIndex : lastIndex) + 1;

                                                props.selection?.onSelectionChange?.(
                                                    Utils.copyAndAddAllIfNeeded(
                                                        props.selection?.selectedItems || [],
                                                        sortedData.slice(startIndex, endIndex),
                                                    ),
                                                );
                                            } else if (event.ctrlKey) {
                                                props.selection?.onSelectionChange?.(
                                                    isSelected
                                                        ? Utils.copyAndRemoveIfNeeded(
                                                              props.selection?.selectedItems || [],
                                                              record,
                                                          )
                                                        : Utils.copyAndAddIfNeeded(
                                                              props.selection?.selectedItems || [],
                                                              record,
                                                          ),
                                                );
                                            } else {
                                                props.selection?.onSelectionChange?.(
                                                    isSelected && props.selection?.selectedItems.length === 1
                                                        ? []
                                                        : [record],
                                                );
                                            }

                                            setLastSelectedIndex(recordIndex);
                                        }}>
                                        {displayValue}
                                    </div>
                                );
                            })}
                            {/*
                                We use a terminator to be able to style the background of a row since there are no
                                row parent divs in a CSS grid and the gaps can't otherwise be styled.
                            */}
                            <div
                                className="data-table__row-terminator"
                                style={{ gridRow: 2 + recordIndex * 1, gridColumn: '1 / -1' }}></div>
                        </div>
                    );
                })}
                {sortedData.length === 0 && <p className="data-table__empty-message">{props.noDataMessage}</p>}
                {sortedData.length > MAX_DISPLAYED_RESULTS && (
                    <p className="data-table__too-large-message">
                        {sortedData.length - MAX_DISPLAYED_RESULTS} results hidden because result set is too large.
                        <br />
                        Search or sort to narrow down the results.
                    </p>
                )}
            </div>
        </Flex>
    );
}
